import {
  boundsOfRects,
  connectorGeometry,
  drawWorldPoints,
  nodeAABB,
  rectCenter,
  rectExpand,
  vRotate,
  visibleRect,
  type BoardNode,
  type DrawNode,
  type Rect,
  type Vec,
} from '@kanvas/board-core';
import { useEffect, useMemo, useRef } from 'react';
import type { BoardDoc } from '../collab/BoardDoc';
import { useDocVersion } from '../collab/hooks';
import { cancelViewportAnimation } from '../state/camera';
import { useEditor } from '../state/store';

const WIDTH = 208;
const HEIGHT = 136;

/**
 * Overview map.
 *
 * Nodes are drawn as flat colour rather than through the real renderer: at map
 * scale the fill is the only information that survives, and one `fillRect` per
 * node keeps the map cheap enough to redraw on every camera change. Lines and
 * strokes are the exception and are drawn as paths, because their bounding
 * boxes say nothing about where they actually run.
 */
export function Minimap({ doc }: { doc: BoardDoc }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const version = useDocVersion(doc);
  const viewport = useEditor((s) => s.viewport);
  const visible = useEditor((s) => s.showMinimap);
  const draggingRef = useRef(false);

  // Everything below is split by what it depends on. The content only changes
  // with the document, but the camera moves every frame of a pan; redrawing
  // every stroke of a well-written board for each of those frames is what made
  // panning lag. So the content is painted once into its own bitmap and the
  // camera frames only copy it and draw the viewport box on top.
  const content = useMemo(() => {
    const nodes = doc.all().filter((n) => n.type !== 'group' && !n.hidden);
    return nodes.length > 0 ? boundsOfRects(nodes.map((n) => nodeAABB(n, doc.lookup))) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, version]);

  const world = useMemo(() => {
    // Always include the camera so the viewport rect stays on the map even when
    // the user pans away from all content.
    const union = boundsOfRects([
      content ?? { x: 0, y: 0, w: 1000, h: 700 },
      visibleRect(viewport),
    ]);
    return rectExpand(union, Math.max(union.w, union.h) * 0.06);
  }, [content, viewport]);

  const dpr = Math.min(window.devicePixelRatio || 1, 2);

  // The content at the scale it would have if the camera sat inside it. The
  // real map is never zoomed in further than that — `world` always contains
  // the content — so copying this bitmap only ever scales it down.
  const layer = useMemo(() => {
    if (!visible || !content) return null;
    const area = rectExpand(content, Math.max(content.w, content.h) * 0.06);
    const canvas = document.createElement('canvas');
    canvas.width = WIDTH * dpr;
    canvas.height = HEIGHT * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const { scale, offsetX, offsetY } = fit(area);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // The bitmap covers the whole map, so `area` is not where it sits on it.
    const extent: Rect = {
      x: area.x - offsetX / scale,
      y: area.y - offsetY / scale,
      w: WIDTH / scale,
      h: HEIGHT / scale,
    };
    const toMapPoint = (p: Vec): Vec => ({
      x: (p.x - extent.x) * scale,
      y: (p.y - extent.y) * scale,
    });

    for (const node of doc.all()) {
      if (node.type === 'group' || node.hidden) continue;

      // Lines and strokes are drawn as paths, not as filled boxes: a diagonal
      // arrow occupies a large bounding box and would read as a solid block.
      const path =
        node.type === 'connector'
          ? connectorGeometry(node, doc.lookup).points
          : node.type === 'draw'
            ? rotatedStroke(node)
            : null;

      if (path) {
        if (path.length < 2) continue;
        ctx.strokeStyle = swatchFor(node);
        ctx.lineWidth = 1;
        ctx.beginPath();
        const first = toMapPoint(path[0]!);
        ctx.moveTo(first.x, first.y);
        for (let i = 1; i < path.length; i++) {
          const p = toMapPoint(path[i]!);
          ctx.lineTo(p.x, p.y);
        }
        ctx.stroke();
        continue;
      }

      const r = nodeAABB(node, doc.lookup);
      const at = toMapPoint(r);
      ctx.fillStyle = swatchFor(node);
      ctx.fillRect(at.x, at.y, Math.max(r.w * scale, 1.5), Math.max(r.h * scale, 1.5));
    }
    return { canvas, extent };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, version, content, visible, dpr]);

  useEffect(() => {
    if (!visible) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Resizing a canvas reallocates it, so only do that when the size moved.
    if (canvas.width !== WIDTH * dpr) canvas.width = WIDTH * dpr;
    if (canvas.height !== HEIGHT * dpr) canvas.height = HEIGHT * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const sheet = doc.getMeta().background || '#F5F6F8';
    ctx.fillStyle = sheet;
    ctx.fillRect(0, 0, WIDTH, HEIGHT);

    const { scale, offsetX, offsetY } = fit(world);
    const toMap = (r: Rect): Rect => ({
      x: offsetX + (r.x - world.x) * scale,
      y: offsetY + (r.y - world.y) * scale,
      w: r.w * scale,
      h: r.h * scale,
    });

    if (layer) {
      const dest = toMap(layer.extent);
      ctx.drawImage(layer.canvas, dest.x, dest.y, dest.w, dest.h);
    }

    const view = toMap(visibleRect(viewport));
    ctx.strokeStyle = '#3B82F6';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(view.x, view.y, view.w, view.h);
    ctx.fillStyle = 'rgba(59,130,246,0.10)';
    ctx.fillRect(view.x, view.y, view.w, view.h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, version, viewport, visible, world, layer, dpr]);

  if (!visible) return null;

  const jumpTo = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const { scale, offsetX, offsetY } = fit(world);
    const boardX = world.x + (clientX - rect.left - offsetX) / scale;
    const boardY = world.y + (clientY - rect.top - offsetY) / scale;
    // Dragging the map is a direct manipulation: it must follow the pointer
    // frame for frame, not ease towards it.
    cancelViewportAnimation();
    useEditor.getState().setViewport((vp) => ({
      ...vp,
      x: boardX - vp.width / 2 / vp.zoom,
      y: boardY - vp.height / 2 / vp.zoom,
    }));
  };

  return (
    <div className="minimap">
      <canvas
        ref={canvasRef}
        role="img"
        aria-label="Миникарта доски"
        style={{ width: WIDTH, height: HEIGHT }}
        onPointerDown={(e) => {
          draggingRef.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          jumpTo(e.clientX, e.clientY);
        }}
        onPointerMove={(e) => {
          if (draggingRef.current) jumpTo(e.clientX, e.clientY);
        }}
        onPointerUp={(e) => {
          draggingRef.current = false;
          if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
          }
        }}
        onPointerCancel={() => {
          draggingRef.current = false;
        }}
      />
    </div>
  );
}

/** Scale and centring offsets that fit a board area into the map. */
function fit(area: Rect): { scale: number; offsetX: number; offsetY: number } {
  const scale = Math.min(WIDTH / area.w, HEIGHT / area.h);
  return {
    scale,
    offsetX: (WIDTH - area.w * scale) / 2,
    offsetY: (HEIGHT - area.h * scale) / 2,
  };
}

/** Stroke polyline as it sits on the board, rotation included. */
function rotatedStroke(node: DrawNode): Vec[] {
  const pts = drawWorldPoints(node);
  if (node.rotation === 0) return pts;
  const c = rectCenter({ x: node.x, y: node.y, w: node.w, h: node.h });
  return pts.map((p) => vRotate(p, c, node.rotation));
}

function swatchFor(node: BoardNode): string {
  switch (node.type) {
    case 'sticky':
      return node.fill;
    case 'shape':
      return node.style.fillStyle === 'none' ? node.style.stroke : node.style.fill;
    case 'draw':
      return node.color;
    case 'connector':
      return node.stroke;
    case 'frame':
      return 'rgba(120,130,145,0.35)';
    case 'notebook':
      return node.coverColor;
    case 'text':
      return node.textStyle.color;
    default:
      return '#9AA3B2';
  }
}
